import { useCallback } from 'react'
import { useIntl } from 'react-intl'

import useRebrandVersion from 'hooks/useRebrandVersion'
import TextButton from 'LuxKit/Button/TextButton'
import LineSearchIcon from 'LuxKit/Icons/line/LineSearchIcon'
import type { TripStop } from 'tripPlanner/api/types'

import type { TripListStopSearchActions } from './tripListTypes'

interface StopSearchActionsProps {
  actions: TripListStopSearchActions
  stop: TripStop
  /** The surface these buttons sit on. Rebrand has no dark stop header, so the
   * dark variant is ignored there and the buttons fall back to default. */
  variant: 'default' | 'dark'
  onOpenStopHotels: (stop: TripStop) => void
  onOpenStopFlights: (stop: TripStop) => void
  onOpenStopExperiences: (stop: TripStop) => void
}

function StopSearchActions({
  actions,
  stop,
  variant,
  onOpenStopHotels,
  onOpenStopFlights,
  onOpenStopExperiences,
}: StopSearchActionsProps) {
  const intl = useIntl()
  const isRebrand = useRebrandVersion() === 'rebrand'
  const buttonVariant = isRebrand ? 'default' : variant
  const handleOpenHotels = useCallback(() => {
    onOpenStopHotels(stop)
  }, [onOpenStopHotels, stop])
  const handleOpenFlights = useCallback(() => {
    onOpenStopFlights(stop)
  }, [onOpenStopFlights, stop])
  const handleOpenExperiences = useCallback(() => {
    onOpenStopExperiences(stop)
  }, [onOpenStopExperiences, stop])

  return <>
    {actions.showHotels && (
      <TextButton
        size="small"
        kind="secondary"
        variant={buttonVariant}
        shape="round"
        startIcon={<LineSearchIcon size="XS" />}
        onClick={handleOpenHotels}
      >
        {intl.formatMessage({
          id: 'jLgwzrb1RgNPjuEyG72C',
          defaultMessage: 'Hotels',
          description: 'Button label to open hotel offers for an empty stop in the trip planner list view',
        })}
      </TextButton>
    )}
    {actions.showFlights && (
      <TextButton
        size="small"
        kind="secondary"
        variant={buttonVariant}
        shape="round"
        startIcon={<LineSearchIcon size="XS" />}
        onClick={handleOpenFlights}
      >
        {intl.formatMessage({
          id: 'lit8PrggdgsNpXvWyi5y',
          defaultMessage: 'Flights',
          description: 'Button label to open flight offers for an empty stop in the trip planner list view',
        })}
      </TextButton>
    )}
    {actions.showExperiences && (
      <TextButton
        size="small"
        kind="secondary"
        variant={buttonVariant}
        shape="round"
        startIcon={<LineSearchIcon size="XS" />}
        onClick={handleOpenExperiences}
      >
        {intl.formatMessage({
          id: '1W9kjs9puIMc4FXxa867',
          defaultMessage: 'Things to do',
          description: 'Button label on a stop row in the trip planner list view that opens the things to do search for that stop',
        })}
      </TextButton>
    )}
  </>
}

export default StopSearchActions
